import { Group, Stack, Text, useMantineTheme } from "@mantine/core"
import { Dropzone, MIME_TYPES, DropzoneProps } from "@mantine/dropzone"
import { IconUpload } from "@tabler/icons-react"
import { noop } from "lodash"
import { useRef, useState } from "react"

import { Asterisk } from "../../../atoms/Asterisk/Asterisk"
import { Button, ButtonProps } from "../../../atoms/Button/Button"
import { FileListItem } from "../FileListItem/FileListItem"

import classes from "./DocumentUploader.module.css"

const ICON_PROPS = {
  size: 48,
  stroke: 1.5,
}

export const DEFAULT_MIME_TYPES = [
  MIME_TYPES.pdf,
  MIME_TYPES.doc,
  MIME_TYPES.docx,
  MIME_TYPES.png,
  MIME_TYPES.jpeg,
]

export const EXCEL_MIME_TYPES = [MIME_TYPES.xls, MIME_TYPES.xlsx]

export const DEFAULT_MAX_SIZE = 3 * 1024 ** 2

export type DocumentUploaderProps = {
  title: string
  helpText?: string
  isLoading?: boolean
  multiple?: boolean
  accept?: string[]
  maxSize?: number
  maxFiles?: number
  disabled?: boolean
  width?: number | string
  showButton?: boolean
  buttonText?: string
  buttonOnClick?: () => void
  iconProps?: {
    size?: number
    stroke?: number
  }
  buttonProps?: Omit<ButtonProps, "onClick">
  titleSize?: string
  helpTextSize?: string
  icon?: React.ReactNode
  justify?: "center" | "left"
  selectedFileName?: string
  error?: boolean
  onRemove?: () => void
  fileListItemSize?: "sm" | "md" | "lg"
  fileListItemIconProps?: {
    size?: number
    strke?: number
  }
  isRemoving?: boolean
  withAsterisk?: boolean
} & DropzoneProps

export const DocumentUploader = ({
  title,
  selectedFileName,
  onRemove,
  helpText,
  isLoading,
  multiple = true,
  accept,
  maxSize = DEFAULT_MAX_SIZE,
  maxFiles,
  disabled,
  width,
  showButton = false,
  buttonText = "Select files",
  buttonOnClick,
  iconProps = ICON_PROPS,
  buttonProps,
  titleSize = "md",
  helpTextSize = "xs",
  icon,
  fileListItemSize = "md",
  fileListItemIconProps,
  justify = "center",
  error = false,
  isRemoving = false,
  withAsterisk = false,
  ...props
}: DocumentUploaderProps) => {
  const { radius } = useMantineTheme()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const openRef = useRef<() => void>(null)

  const titleComponent = (
    <UploadTitle
      title={title}
      helpText={helpText}
      titleSize={titleSize}
      helpTextSize={helpTextSize}
      withAsterisk={withAsterisk}
    />
  )

  return (
    <div className={classes.wrapper} data-error={error}>
      {selectedFileName ? (
        <FileListItem
          fileName={selectedFileName}
          onRemove={onRemove ?? noop}
          w="100%"
          h="100%"
          size={fileListItemSize}
          iconProps={fileListItemIconProps}
          isRemoving={isRemoving}
          isLoading={isLoading}
        />
      ) : (
        <>
          <Dropzone
            openRef={openRef}
            maxSize={maxSize}
            maxFiles={maxFiles}
            accept={accept || DEFAULT_MIME_TYPES}
            multiple={multiple}
            loading={isLoading}
            onReject={(fileRejectInfo) => {
              setErrorMessage(
                fileRejectInfo.map((file) => file.errors[0].message).join(", ")
              )
            }}
            loaderProps={{ type: "oval" }}
            disabled={disabled}
            w={width}
            className={classes.dropzone}
            {...props}
          >
            <Dropzone.Accept>
              <Group wrap="nowrap" justify={justify}>
                <IconUpload {...iconProps} width={iconProps.size} />
                {titleComponent}
              </Group>
            </Dropzone.Accept>
            <Dropzone.Reject>
              <FileListItem
                fileName={selectedFileName ?? ""}
                onRemove={onRemove ?? noop}
                error={errorMessage ?? undefined}
                w="100%"
                h="100%"
                size={fileListItemSize}
              />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <Group wrap="nowrap" justify={justify}>
                {icon ? (
                  icon
                ) : (
                  <IconUpload {...iconProps} width={iconProps.size} />
                )}
                {titleComponent}
              </Group>
            </Dropzone.Idle>
          </Dropzone>
          {showButton && (
            <Button
              className={classes.control}
              radius={radius.md}
              onClick={
                buttonOnClick
                  ? () => buttonOnClick()
                  : () => openRef.current?.()
              }
              {...buttonProps}
            >
              {buttonText}
            </Button>
          )}
        </>
      )}
    </div>
  )
}

const UploadTitle = ({
  title,
  helpText,
  titleSize = "md",
  helpTextSize = "xs",
  withAsterisk = false,
}: {
  title: string
  helpText?: string
  titleSize?: string
  helpTextSize?: string
  withAsterisk?: boolean
}) => {
  return title ? (
    <Stack gap={0} justify="center" maw="80%">
      <Text fz={titleSize} inline>
        {title} {withAsterisk && <Asterisk />}
      </Text>
      {helpText && (
        <Text fz={helpTextSize} c="dimmed" inline mt={7}>
          {helpText}
        </Text>
      )}
    </Stack>
  ) : null
}
